// src/voice/voiceInputRuntime.js
//
// EDEN voice-input runtime intelligence — shared, pure helpers for the
// microphone/SpeechRecognition input path.
//
// This module exists because the browser-native SpeechRecognition service is
// environment-dependent: the packaged Cortex desktop shell is a Safari Web
// App (WebKit "Add to Dock" wrapper), where recognition support is
// conditional even when getUserMedia succeeds. The UI must therefore never
// treat "microphone acquired" as "recognition ready" — ready state requires
// a confirmed recognition.onstart.
//
// Everything here is deterministic and content-free: classifications carry
// error codes and fixed user copy only, never transcripts or spoken content.
//
// This file must stay byte-identical between the Client and Superadmin apps.

export const UNSUPPORTED_RUNTIME_MESSAGE_SHELL =
  "Voice input isn't supported in this version of Cortex. You can still type.";
export const UNSUPPORTED_RUNTIME_MESSAGE_BROWSER =
  "Voice input isn't available in this browser. You can still type.";
export const UNSUPPORTED_RUNTIME_MESSAGE_IOS_WEB_APP =
  "Speech recognition isn't available in this installed iPhone web app. Open Cortex in Safari or continue typing in Ask Eden.";
export const UNSUPPORTED_RUNTIME_MESSAGE_CHROME_IOS =
  "Speech recognition isn't available in Chrome on this iPhone. You can still type in Ask Eden.";

/**
 * Detect the hosting runtime for voice input. Pure given (win, nav) so tests
 * can exercise every shell shape.
 */
export function detectVoiceRuntime(
  win = typeof window !== "undefined" ? window : undefined,
  nav = typeof navigator !== "undefined" ? navigator : undefined
) {
  const userAgent = String(nav?.userAgent || "");
  const isElectron =
    /electron/i.test(userAgent) || Boolean(win?.process?.versions?.electron);
  const standaloneDisplay = Boolean(
    win?.matchMedia?.("(display-mode: standalone)")?.matches
  );
  const iosStandalone = nav?.standalone === true;
  const standalone = standaloneDisplay || iosStandalone;
  const isIOS =
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (nav?.platform === "MacIntel" && Number(nav?.maxTouchPoints || 0) > 1);
  const isChromeIOS = isIOS && /CriOS/i.test(userAgent);
  const isFirefoxIOS = isIOS && /FxiOS/i.test(userAgent);
  const isEdgeIOS = isIOS && /EdgiOS/i.test(userAgent);
  const isWebKit =
    /AppleWebKit/i.test(userAgent) && !/Chrome|Chromium|CriOS|Edg\//i.test(userAgent);
  const isIOSWebApp = isIOS && standalone;
  // Keep the macOS installed Safari app separate from iPhone home-screen
  // mode. Their SpeechRecognition capabilities and settings surfaces differ.
  const isSafariWebApp = standalone && isWebKit && !isIOS;
  const isDesktopShell = isElectron || isSafariWebApp;
  const isSafariIOS =
    isIOS && /Safari/i.test(userAgent) && !isChromeIOS && !isFirefoxIOS && !isEdgeIOS;

  return {
    isElectron,
    isSafariWebApp,
    isIOSWebApp,
    isSafariIOS,
    isChromeIOS,
    isFirefoxIOS,
    isEdgeIOS,
    isIOS,
    isWebKit,
    standalone,
    isDesktopShell,
    shellKind: isElectron
      ? "electron"
      : isIOSWebApp
      ? "ios-web-app"
      : isChromeIOS
      ? "chrome-ios"
      : isSafariIOS
      ? "safari-ios"
      : isSafariWebApp
      ? "safari-web-app"
      : "browser",
    speechRecognitionSupported: Boolean(
      win?.SpeechRecognition || win?.webkitSpeechRecognition
    ),
  };
}

export function resolveUnsupportedRuntimeMessage(runtime) {
  if (runtime?.isIOSWebApp) {
    return UNSUPPORTED_RUNTIME_MESSAGE_IOS_WEB_APP;
  }
  if (runtime?.isChromeIOS) {
    return UNSUPPORTED_RUNTIME_MESSAGE_CHROME_IOS;
  }
  return runtime?.isDesktopShell
    ? UNSUPPORTED_RUNTIME_MESSAGE_SHELL
    : UNSUPPORTED_RUNTIME_MESSAGE_BROWSER;
}

const SERVICE_UNAVAILABLE_MESSAGE =
  "Voice recognition is temporarily unavailable in this environment. You can still type.";

export function resolveRecognitionUnavailableMessage(runtime) {
  if (runtime?.isIOSWebApp) {
    return UNSUPPORTED_RUNTIME_MESSAGE_IOS_WEB_APP;
  }
  if (runtime?.isChromeIOS) {
    return UNSUPPORTED_RUNTIME_MESSAGE_CHROME_IOS;
  }
  return SERVICE_UNAVAILABLE_MESSAGE;
}

export function resolvePermissionDeniedMessage(runtime) {
  if (runtime?.isChromeIOS) {
    return "Microphone access is blocked. On iPhone, open Settings > Apps > Chrome > Microphone, allow access, then tap Talk again. You can still type.";
  }
  if (runtime?.isIOSWebApp || runtime?.isSafariIOS) {
    return "Microphone access is blocked. On iPhone, allow Microphone for Cortex in Safari Website Settings or Settings > Apps > Safari, then tap Talk again. You can still type.";
  }
  if (runtime?.isSafariWebApp || runtime?.isWebKit) {
    return "Microphone access is blocked. Allow Microphone in Safari's settings for this website, then tap Talk again. You can still type.";
  }
  return "Microphone access is blocked. Allow it in this browser's site settings, then tap Talk again. You can still type.";
}

/**
 * Map a SpeechRecognition error value onto a precise state code and
 * actionable user copy. Raw browser errors are never shown to users.
 *
 * Returns null when the event is not a failure (our own abort during a
 * manual stop or teardown) — callers must not enter an error state for null.
 */
export function classifyRecognitionError(rawError, { runtime, manualStop = false } = {}) {
  const error = String(rawError || "").trim().toLowerCase();

  if (error === "aborted") {
    if (manualStop) return null;
    // Genuine unexpected interruption: calm, neutral copy. Browser event
    // names (aborted / recognition_aborted) are internal codes only and are
    // never surfaced to the user.
    return {
      code: "recognition_aborted",
      message: "Voice capture stopped unexpectedly. Tap Talk to try again.",
    };
  }

  switch (error) {
    case "audio-capture":
      return {
        code: "audio_capture_failed",
        message:
          "Cortex couldn't access the selected microphone. Check the device and try again.",
      };
    case "network":
      // Inside the packaged shell, "network" is the signature of the
      // recognition service being unavailable to the wrapper — not of the
      // user's connectivity.
      return runtime?.isDesktopShell
        ? {
            code: "recognition_service_unavailable",
            message: resolveRecognitionUnavailableMessage(runtime),
          }
        : {
            code: "recognition_network_failed",
            message:
              "Voice recognition couldn't connect. Check your connection and try again.",
          };
    case "no-speech":
      return {
        code: "no_speech",
        message: "No speech detected. Tap Talk to try again.",
      };
    case "not-allowed":
      return {
        code: "microphone_blocked",
        message: resolvePermissionDeniedMessage(runtime),
      };
    case "service-not-allowed":
      return {
        code: "recognition_service_unavailable",
        message: resolveRecognitionUnavailableMessage(runtime),
      };
    case "language-not-supported":
      return {
        code: "language_not_supported",
        message: resolveRecognitionUnavailableMessage(runtime),
      };
    default:
      return {
        code: error || "voice_capture_error",
        message: "Voice input hit an unexpected error. You can still type.",
      };
  }
}

/**
 * Recognize EDEN's ready-cue greeting text ("I'm listening." /
 * "I'm listening, David.") so an error handler can retract a stale greeting
 * without ever touching a real assistant reply.
 */
export function isEdenGreetingMessage(text) {
  return /^I'm listening(?:, [^.\n]+)?\.$/.test(String(text || "").trim());
}

/**
 * Delegated-planning candidate detection (shared, byte-identical).
 *
 * A pasted opportunity brief / RFP with an explicit planning instruction must
 * reach the EDEN backend for durable delegation — local action-intent
 * routing (e.g. "TikTok" → open TikTok Analytics) and planner-revision
 * navigation must NOT intercept it. Mirrors the backend detector's spirit:
 * a planning verb + a plan noun, or a long pasted brief that mentions a
 * plan noun.
 */
const DELEGATION_PLAN_NOUN_PATTERN =
  /\b(media plan|media recommendation|draft plan|campaign plan|media strategy|plan for review)\b/i;
const DELEGATION_PLAN_VERB_PATTERN =
  /\b(build|draft|create|prepare|start|turn|use|develop|put together|assemble|generate)\b/i;

export function looksLikeDelegatedPlanningRequest(text) {
  const value = String(text || "").trim();
  if (!value) return false;
  if (!DELEGATION_PLAN_NOUN_PATTERN.test(value)) return false;
  return DELEGATION_PLAN_VERB_PATTERN.test(value) || value.length >= 400;
}
